<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { StandardFernwaermemix, StandardFernwaermemixCollection } from '@/models/StandardFernwaermemix';
import { NutzungsartCollection, type Nutzungsart } from '@/models/Nutzungsart';
import { instance as api } from '@/helpers/api';
import { FilterMatchMode } from '@primevue/core/api';
import type { DataTableFilterEvent } from 'primevue/datatable';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import KpGemeindeSelect from '@/components/KpGemeindeSelect.vue';
import KpJahrSelect from '@/components/KpJahrSelect.vue';
import KpEnergieWaermequelleSelect from '@/components/KpEnergieWaermequelleSelect.vue';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import CopyJahrDialog from '@/components/CopyJahrDialog.vue';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'kanton_kuerzel' },
    { direction: 'asc', property: 'gemeinde' },
    { direction: 'desc', property: 'jahr' },
    { direction: 'asc', property: 'energie_waermequelle_bezeichnung' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new StandardFernwaermemixCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

const gemeindeen: Ref<Nutzungsart[]> = ref([]);
onMounted(async () => {
    if (collection.loading.value !== true) {
        collection.query();
    }
    const col = new NutzungsartCollection();
    col.setQueryParams({
        sort: [
            {
                property: 'reihenfolge',
                direction: 'asc',
            },
        ],
    });
    await col.query();
    col.each((m) => gemeindeen.value.push(m));
    loadAnteilTotal();
});

function editRecord(r: StandardFernwaermemix) {
    router.push({ name: 'bg_jw_standard_fernwaermemix_edit', params: { id: r?.id } });
}

async function deleteRecord(r: StandardFernwaermemix) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_jw_standard_fernwaermemix_edit', params: { id: 'new' } });
}

type Filter = {
    property: string;
    value: any;
    id: string;
};
const filters = ref({
    jahr: { value: null, matchMode: FilterMatchMode.EQUALS },
    kanton_id: { value: null, matchMode: FilterMatchMode.EQUALS },
    gemeinde_id: { value: null, matchMode: FilterMatchMode.EQUALS },
    energie_waermequelle_id: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const filter: Ref<Filter[]> = ref([]);
function onFilterChange(filterEvent: DataTableFilterEvent) {
    const prop = 'value';
    filter.value = [];
    for (const key in filterEvent.filters) {
        // @ts-expect-error it's save, do it!
        const value = filterEvent.filters[key].hasOwnProperty(prop) ? filterEvent.filters[key][prop] : null;
        filter.value.push({
            property: key,
            id: key,
            value,
        });
    }
    collection.setQueryParams({ filter: filter.value });
    collection.query();
    loadAnteilTotal();
}

const anteilTotal = ref('');
async function loadAnteilTotal() {
    const res = await api().post('/StandardFernwaermemix/getAnteilTotal', {
        filter: filter.value,
    });
    if (res && res.data && res.data.total) {
        anteilTotal.value = Number.parseFloat(res.data.total).toFixed(2);
    } else {
        anteilTotal.value = '0';
    }
}

const showCopyDlg = ref(false);
function openCopyDialog() {
    showCopyDlg.value = true;
}

async function copy(targetJahr: number, kantonId: number, sourceJahr: number | null) {
    showCopyDlg.value = false;
    try {
        await api().post('/StandardFernwaermemix/copy', {
            targetJahr,
            kantonId,
            sourceJahr,
        });
        toast.add({
            severity: 'success',
            summary: getI18n().t('erfolg'),
            detail: getI18n().t('eintraege_kopiert'),
        });
        await reloadCollection();
        loadAnteilTotal();
    } catch (e) {
        errToToast(e, toast);
    }
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:filters="filters"
            v-model:table-sort="tableSort"
            :title="$t('standard_fernwaermemix')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            filterDisplay="row"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            showCopyBtn
            @editrecord="editRecord($event as StandardFernwaermemix)"
            @deleterecord="deleteRecord($event as StandardFernwaermemix)"
            @new="newRecord"
            @copy="openCopyDialog()"
            @filter="onFilterChange"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column
                field="kanton_id"
                sortable
                :header="$t('kanton')"
                sort-field="kanton_kuerzel"
                :showFilterMenu="false"
            >
                <template #body="slotProps">{{ slotProps.data.kanton_kuerzel }}</template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpMandantKantonSelect
                        v-model="filterModel.value"
                        fluid
                        @change="filterCallback()"
                    ></KpMandantKantonSelect>
                </template>
            </Column>
            <Column
                field="gemeinde_id"
                sortable
                :header="$t('gemeinde')"
                sort-field="gemeinde"
                :show-filter-menu="false"
            >
                <template #body="slotProps">{{ slotProps.data.gemeinde }}</template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpGemeindeSelect
                        :mandant="true"
                        :label="null"
                        fluid
                        v-model="filterModel.value"
                        @change="filterCallback()"
                    ></KpGemeindeSelect>
                </template>
            </Column>
            <Column field="jahr" :header="$t('jahr')" filter-field="jahr" sortable :showFilterMenu="false">
                <template #body="slotProps">
                    <div>{{ slotProps.data.jahr }}</div>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpJahrSelect fluid v-model="filterModel.value" @change="filterCallback()"></KpJahrSelect>
                </template>
            </Column>
            <Column
                field="energie_waermequelle_id"
                :header="$t('energie_waermequelle')"
                sortable
                sort-field="energie_waermequelle_bezeichnung"
                :showFilterMenu="false"
            >
                <template #body="slotProps">
                    <div>{{ $t(slotProps.data.energie_waermequelle_i18n_key) }}</div>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpEnergieWaermequelleSelect
                        fluid
                        v-model="filterModel.value"
                        @change="filterCallback()"
                    ></KpEnergieWaermequelleSelect>
                </template>
            </Column>
            <Column field="anteil" :header="$t('anteil_proc')" sortable></Column>
            <ColumnGroup type="footer">
                <Row>
                    <Column footer="Total:" :colspan="5" footerStyle="text-align:right" />
                    <Column :footer="anteilTotal" />
                    <Column />
                </Row>
            </ColumnGroup>
        </KpBaseCollectionGrid>
        <CopyJahrDialog v-model="showCopyDlg" with-from-year @copy="copy"></CopyJahrDialog>
    </div>
</template>
